import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../api/axios";
import PropertyForm from "../components/PropertyForm";
import SearchFilter from "../components/SearchFilter";
import BulkActions from "../components/BulkActions";
import ExportData from "../components/ExportData";
import StatusBadge from "../components/StatusBadge";
import { exportSingleRecord } from "../utils/pdfExport";
import Pagination from "../components/Pagination";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useToastContext } from "../contexts/ToastContext";
import getImageUrl from "../utils/imageUrl";

export default function Properties() {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const toast = useToastContext();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProperty, setEditProperty] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [viewMode, setViewMode] = useState('cards');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  const totalPages = Math.ceil(filteredProperties.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProperties = filteredProperties.slice(startIndex, endIndex);

  const fetchProperties = () => {
    setLoading(true);
    api.get("/properties")
      .then((res) => {
        const propertyData = res.data.data || res.data || [];
        setProperties(propertyData);
        setFilteredProperties(propertyData);
      })
      .catch((err) => {
        console.error("Error fetching properties:", err.message);
        setProperties([]);
        setFilteredProperties([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const handleEdit = (property) => {
    setEditProperty(property);
    setShowForm(true);
  };

  const handleViewDetail = (property) => {
    setSelectedProperty(property);
    setShowDetailModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm_delete_property'))) {
      try {
        await api.delete(`/properties/${id}`);
        toast.success(t('property_deleted_success'));
        fetchProperties();
      } catch (error) {
        const errorMsg = error.response?.data?.message || t('failed_delete_property');
        toast.error(errorMsg);
      }
    }
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditProperty(null);
    fetchProperties();
  };

  const handleSearch = (searchTerm) => {
    const filtered = properties.filter(property =>
      property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.address.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredProperties(filtered);
  };

  const handleFilter = (filterValue) => {
    if (!filterValue) {
      setFilteredProperties(properties);
    } else {
      const filtered = properties.filter(property => property.status === filterValue);
      setFilteredProperties(filtered);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await api.put(`/properties/${id}`, { status });
      fetchProperties();
    } catch (error) {
      console.error('Error updating property status:', error);
    }
  };

  const handleToggleFullBooking = async (property) => {
    try {
      const res = await api.patch(`/properties/${property.id}/toggle-full-booking`);
      const newState = res.data?.data?.is_full_booking;
      toast.success(newState ? 'Unit set to Full Booking 🔴' : 'Unit set to Available 🟢');
      fetchProperties();
    } catch (error) {
      console.error('Error toggling full booking:', error);
      toast.error('Failed to update Full Booking status');
    }
  };

  const statusFilters = [
    { value: 'available', label: t('available') },
    { value: 'booking', label: t('booking') },
    { value: 'rented', label: t('rented') },
    { value: 'maintenance', label: t('maintenance') }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4 animate-spin" />
          <p className="text-gray-600 text-lg">{t('loading_properties')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6`}>
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}>{t('property_management')}</h1>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mt-1`}>{t('manage_all_properties')}</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
            <ExportData data={filteredProperties} filename="properties" title={t('properties_management_report')} includeStats={true} language={language} />
            <button
              onClick={() => setShowForm(true)}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                }`}
            >
              <i className="fas fa-plus mr-2"></i>
              {t('add_property')}
            </button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <SearchFilter
            onSearch={handleSearch}
            onFilter={handleFilter}
            filters={statusFilters}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{t('view')}:</span>
          <button
            onClick={() => setViewMode('cards')}
            className={`px-3 py-2 rounded-lg text-sm transition-all ${viewMode === 'cards'
              ? (isDark ? 'bg-gray-600 text-white' : 'bg-blue-600 text-white')
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
          >
            <i className="fas fa-th-large mr-1"></i> {t('cards')}
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-2 rounded-lg text-sm transition-all ${viewMode === 'table'
              ? (isDark ? 'bg-gray-600 text-white' : 'bg-blue-600 text-white')
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
          >
            <i className="fas fa-table mr-1"></i> {t('table')}
          </button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'cards' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentProperties.map((property) => {
            const isFullBooking = property.is_full_booking === true || property.is_full_booking === 1 || property.is_full_booking === "1";
            return (
              <div key={property.id} className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl transition-all duration-300 h-96 flex flex-col">
                {/* Image */}
                <div className="h-48 bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center relative overflow-hidden">
                  {(() => {
                    // Priority: all_images[0] > image_url > image > placeholder
                    let imageUrl = null;

                    if (property.all_images && property.all_images.length > 0) {
                      const primaryImage = property.all_images.find(img => img.is_primary);
                      imageUrl = primaryImage ? primaryImage.url : property.all_images[0].url;
                    } else if (property.image_url) {
                      imageUrl = property.image_url;
                    } else if (property.image) {
                      imageUrl = getImageUrl(property.image);
                    }

                    return imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={property.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const placeholder = e.target.nextElementSibling;
                          if (placeholder) placeholder.style.display = 'flex';
                        }}
                      />
                    ) : null;
                  })()}
                  <div
                    className="w-full h-full flex items-center justify-center text-white text-6xl"
                    style={{
                      display: (() => {
                        if (property.all_images && property.all_images.length > 0) return 'none';
                        if (property.image_url) return 'none';
                        if (property.image) return 'none';
                        return 'flex';
                      })()
                    }}
                  >
                    🏢
                  </div>
                  {isFullBooking && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg transform -rotate-12 border-2 border-white">
                        Full Booking
                      </span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 truncate">{property.name}</h3>
                  <p className="text-sm text-gray-600 mb-3 flex items-center truncate">
                    <i className="fas fa-map-marker-alt mr-2 text-red-500" />
                    {property.city}
                  </p>
                  <div className="flex justify-between items-center mb-4">
                    <span className={`text-xl font-bold truncate ${isDark ? 'text-gray-300' : 'text-green-600'}`}>
                      Rp {Number(property.price).toLocaleString()}
                    </span>
                    <span className="text-sm text-gray-500">
                      {property.bedrooms}{t('br')} • {property.bathrooms}{t('ba')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    {t('size')}: {property.size}m²
                  </p>
                  <div className="flex-1"></div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewDetail(property)}
                      className="flex-1 py-2 px-3 rounded-lg text-sm font-medium bg-green-500 hover:bg-green-600 text-white transition-all duration-200"
                    >
                      <i className="fas fa-eye mr-1" /> Detail
                    </button>
                    <button
                      onClick={() => handleEdit(property)}
                      className="py-2 px-3 rounded-lg text-sm font-medium bg-blue-500 hover:bg-blue-600 text-white transition-all duration-200"
                    >
                      <i className="fas fa-edit" />
                    </button>
                    <button
                      onClick={() => handleDelete(property.id)}
                      className="py-2 px-3 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-all duration-200"
                    >
                      <i className="fas fa-trash" />
                    </button>
                    <button
                      onClick={() => handleToggleFullBooking(property)}
                      title={isFullBooking ? "Turn Off Full Booking" : "Turn On Full Booking"}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 ${isFullBooking
                        ? 'bg-red-100 text-red-600 hover:bg-red-200 border border-red-200'
                        : 'bg-green-100 text-green-600 hover:bg-green-200 border border-green-200'
                        }`}
                    >
                      <i className={`fas ${isFullBooking ? 'fa-toggle-on' : 'fa-toggle-off'} text-lg`} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{t('property')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{t('location')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{t('price')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Full Booking</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{t('status')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{t('actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentProperties.map((property) => (
                  <tr key={property.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">{property.name}</div>
                      <div className="text-xs text-gray-500">ID: {property.id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{property.city}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-bold text-green-600">Rp {Number(property.price).toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const isFullBooking = property.is_full_booking === true || property.is_full_booking === 1 || property.is_full_booking === "1";
                        return (
                          <button
                            onClick={() => handleToggleFullBooking(property)}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 flex items-center gap-1 ${isFullBooking
                              ? 'bg-red-100 text-red-600 hover:bg-red-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                          >
                            <i className={`fas ${isFullBooking ? 'fa-toggle-on' : 'fa-toggle-off'} text-sm`} />
                            {isFullBooking ? 'ON' : 'OFF'}
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge
                        currentStatus={property.status}
                        statusOptions={['available', 'booking', 'rented', 'maintenance']}
                        onStatusChange={(newStatus) => handleStatusUpdate(property.id, newStatus)}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleViewDetail(property)}
                          className="px-2 py-1 rounded-lg text-xs font-medium bg-green-500 hover:bg-green-600 text-white"
                        >
                          <i className="fas fa-eye mr-1"></i>Detail
                        </button>
                        <button
                          onClick={() => handleEdit(property)}
                          className="px-2 py-1 rounded-lg text-xs font-medium bg-blue-500 hover:bg-blue-600 text-white"
                        >
                          {t('edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(property.id)}
                          className="px-2 py-1 rounded-lg text-xs font-medium bg-red-500 hover:bg-red-600 text-white"
                        >
                          {t('delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={filteredProperties.length}
      />

      {showForm && (
        <PropertyForm
          property={editProperty}
          onSave={handleFormSave}
          onCancel={() => { setShowForm(false); setEditProperty(null); }}
        />
      )}

      {/* Property Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedProperty && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-[60] p-4 pt-28 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl max-w-4xl w-full mb-8`}
            >
              {/* Header */}
              <div className={`${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-gray-200'} border-b px-6 py-4 flex justify-between items-center`}>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <i className="fas fa-building mr-2 text-blue-500"></i>
                  Detail Unit: {selectedProperty.name}
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className={`${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'} text-2xl transition-colors`}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Images Section */}
                <div className="space-y-4">
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center`}>
                    <i className="fas fa-images mr-2 text-green-500"></i>
                    Foto Unit ({selectedProperty.all_images?.length || 1})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedProperty.all_images && selectedProperty.all_images.length > 0 ? (
                      selectedProperty.all_images.map((img, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={img.url}
                            alt={`${selectedProperty.name} - ${index + 1}`}
                            className="w-full h-48 object-cover rounded-lg shadow-md group-hover:shadow-lg transition-shadow"
                            onError={(e) => {
                              console.error('Image failed to load:', img.url);
                              e.target.src = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?q=80&w=400&auto=format&fit=crop';
                            }}
                          />
                          {img.is_primary && (
                            <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                              <i className="fas fa-star mr-1"></i>Utama
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="col-span-full">
                        <img
                          src={(() => {
                            if (selectedProperty.image_url) return selectedProperty.image_url;
                            if (selectedProperty.image) return getImageUrl(selectedProperty.image);
                            return 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?q=80&w=400&auto=format&fit=crop';
                          })()}
                          alt={selectedProperty.name}
                          className="w-full h-64 object-cover rounded-lg shadow-md"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?q=80&w=400&auto=format&fit=crop';
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Property Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Info */}
                  <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-4 space-y-3`}>
                    <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center`}>
                      <i className="fas fa-info-circle mr-2 text-blue-500"></i>
                      Informasi Dasar
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>ID Unit:</span>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>#{selectedProperty.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Nama:</span>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedProperty.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Kota:</span>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedProperty.city}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Status:</span>
                        <StatusBadge
                          currentStatus={selectedProperty.status}
                          statusOptions={['available', 'booking', 'rented', 'maintenance']}
                          onStatusChange={(newStatus) => handleStatusUpdate(selectedProperty.id, newStatus)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Specifications */}
                  <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-4 space-y-3`}>
                    <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center`}>
                      <i className="fas fa-ruler-combined mr-2 text-purple-500"></i>
                      Spesifikasi
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Kamar Tidur:</span>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedProperty.bedrooms} kamar</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Kamar Mandi:</span>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedProperty.bathrooms} kamar</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Luas:</span>
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedProperty.size} m²</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Harga/hari:</span>
                        <span className="font-bold text-green-600 text-lg">Rp {Number(selectedProperty.price).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-4`}>
                  <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-3 flex items-center`}>
                    <i className="fas fa-align-left mr-2 text-orange-500"></i>
                    Deskripsi
                  </h4>
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                    {selectedProperty.description || 'Tidak ada deskripsi tersedia.'}
                  </p>
                </div>

                {/* Address */}
                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-4`}>
                  <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-3 flex items-center`}>
                    <i className="fas fa-map-marker-alt mr-2 text-red-500"></i>
                    Alamat Lengkap
                  </h4>
                  <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                    {selectedProperty.address || 'Alamat tidak tersedia.'}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className={`${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-t px-6 py-4 flex justify-end gap-3`}>
                <button
                  onClick={() => handleEdit(selectedProperty)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 text-white ${isDark ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                >
                  <i className="fas fa-edit mr-2"></i>Edit Unit
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${isDark ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                >
                  Tutup
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}